// ============================================================
// Sections Admin shop, Factions et plateformes (Statistiques) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite 1.0.26 :
//   - admin shop : transactions de l'argent (Vault) de l'admin shop, achats et ventes ;
//   - factions : base de FactionApi (saison en cours, sans Wilderness, SafeZone ni WarZone), joueurs en faction
//     depuis le satellite 1.0.28 ;
//   - plateformes : plateforme et mode de jeu envoyés par le proxy à la connexion de chaque joueur.

/** Plateformes, dans l'ordre fixe qui donne aussi leur couleur (la couleur suit la plateforme, pas son rang). */
export const PLATFORM_ORDER = ['windows', 'android', 'ios', 'xbox', 'playstation', 'switch', 'macos', 'fireos', 'linux', 'unknown'] as const;

const PLATFORM_LABELS: Record<string, string> = {
  windows: 'PC Windows',
  android: 'Android',
  ios: 'iPhone et iPad',
  xbox: 'Xbox',
  playstation: 'PlayStation',
  switch: 'Nintendo Switch',
  macos: 'Mac',
  fireos: 'Amazon Fire',
  linux: 'Linux',
  unknown: 'Inconnue',
};

export function platformLabel(key: string): string {
  return PLATFORM_LABELS[key] ?? key;
}

/** Couleur d'une plateforme : les 8 teintes validées dans l'ordre fixe, gris pour Linux et l'inconnue. */
export function platformColor(key: string): string {
  const i = (PLATFORM_ORDER as readonly string[]).indexOf(key);
  return i >= 0 && i < 8 ? `var(--job-${i + 1})` : 'var(--job-other)';
}

/** Famille d'une plateforme : PC, mobile ou console (bandeau de résumé). */
export function platformFamily(key: string): 'pc' | 'mobile' | 'console' | 'autre' {
  if (key === 'windows' || key === 'macos' || key === 'linux') return 'pc';
  if (key === 'android' || key === 'ios' || key === 'fireos') return 'mobile';
  if (key === 'xbox' || key === 'playstation' || key === 'switch') return 'console';
  return 'autre';
}

export const INPUT_ORDER = ['keyboard', 'touch', 'gamepad', 'unknown'] as const;

const INPUT_LABELS: Record<string, string> = {
  keyboard: 'Clavier et souris',
  touch: 'Tactile',
  gamepad: 'Manette',
  unknown: 'Inconnu',
};

export function inputLabel(key: string): string {
  return INPUT_LABELS[key] ?? key;
}

export function inputColor(key: string): string {
  const i = (INPUT_ORDER as readonly string[]).indexOf(key);
  return i >= 0 && i < 3 ? `var(--job-${i + 1})` : 'var(--job-other)';
}

/** Parts en pour cent d'une liste de valeurs (0 partout pour un total nul). */
export function shares(values: readonly number[]): number[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  return values.map((v) => (total > 0 ? (Math.max(0, v) * 100) / total : 0));
}

// ==================== Factions ====================

/** Part de {@code part} dans {@code total}, au dixième, à la française (« 62,5 % ») ; « 0 % » si le total est nul. */
export function percentLabel(part: number, total: number): string {
  if (!(total > 0)) return '0 %';
  return `${(Math.round((Math.max(0, part) * 1000) / total) / 10).toLocaleString('fr-FR')} %`;
}

const BRANCH_LABELS: Record<string, string> = {
  global: 'Global',
  pillage: 'Pillage',
  economy: 'Économie',
  defense: 'Défense',
};

export function branchLabel(key: string): string {
  return BRANCH_LABELS[key] ?? key;
}

export const BRANCH_ORDER = ['global', 'pillage', 'economy', 'defense'] as const;

export function branchColor(key: string): string {
  const i = (BRANCH_ORDER as readonly string[]).indexOf(key);
  return i >= 0 ? `var(--job-${i + 1})` : 'var(--job-other)';
}

const LEAGUE_LABELS: Record<string, string> = {
  unranked: 'Non classée',
  bronze: 'Bronze',
  silver: 'Argent',
  gold: 'Or',
  platinum: 'Platine',
  diamond: 'Diamant',
};

export function leagueLabel(key: string): string {
  return LEAGUE_LABELS[key] ?? key;
}

/** Tris du tableau des factions. */
export type FactionSort = 'techLevel' | 'members' | 'bank' | 'experience' | 'seasonalElo' | 'createdAt' | 'name';

export interface SortableFaction {
  id: string;
  name?: string;
  members: number;
  techLevel: number;
  bank: number;
  experience: number;
  seasonalElo: number;
  createdAt?: number;
}

/** Trie une copie : nombres du plus grand au plus petit, nom de A à Z, création de la plus récente ; id à égalité. */
export function sortFactions<T extends SortableFaction>(rows: readonly T[], by: FactionSort): T[] {
  const out = [...rows];
  out.sort((a, b) => {
    let d = 0;
    if (by === 'name') d = (a.name ?? '').localeCompare(b.name ?? '', 'fr');
    else if (by === 'createdAt') d = (b.createdAt ?? 0) - (a.createdAt ?? 0);
    else d = (b[by] as number) - (a[by] as number);
    return d !== 0 ? d : a.id.localeCompare(b.id);
  });
  return out;
}

/** Filtre par nom (sans casse ni accents). */
export function filterFactions<T extends SortableFaction>(rows: readonly T[], query: string): T[] {
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = norm(query.trim());
  if (!q) return [...rows];
  return rows.filter((r) => norm(r.name ?? '').includes(q));
}

// ==================== Argent ====================

/** Montant compact : 950 $, 12 k$, 3,2 M$. */
export function formatMoneyShort(amount: number): string {
  if (!Number.isFinite(amount)) return '0 $';
  const a = Math.abs(amount);
  const fmt = (v: number, unit: string) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: Math.abs(v) < 10 ? 1 : 0 })} ${unit}$`;
  if (a >= 1e9) return fmt(amount / 1e9, 'Md ');
  if (a >= 1e6) return fmt(amount / 1e6, 'M ');
  if (a >= 1e4) return fmt(amount / 1e3, 'k ');
  return `${Math.round(amount).toLocaleString('fr-FR')} $`;
}

/**
 * Ce que la page dit d'une réponse en erreur. 404 : le satellite n'a pas encore ces routes (version d'avant la
 * 1.0.26). 403 : le grade n'a pas le droit. Sinon le message du satellite.
 */
export function sectionError(status: number | undefined, error: string | undefined, what: string): string {
  if (status === 404) return `Le satellite de ce projet ne sert pas encore ${what} : cela arrive avec le satellite 1.0.26.`;
  if (status === 403) return "Ton grade n'a pas accès à cette section des statistiques.";
  return error?.trim() || 'Les statistiques n\'ont pas pu être lues.';
}
